import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

const RetellAgentsDetails = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [retellAgents, setRetellAgents] = useState([]);
  const [showRetellAgents, setShowRetellAgents] = useState(false);
  const [loadingRetellAgents, setLoadingRetellAgents] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [billingDetails, setBillingDetails] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [adminAgents, setAdminAgents] = useState([]);
  const [adminAgentsBilling, setAdminAgentsBilling] = useState({});
  const [loadingAdminAgents, setLoadingAdminAgents] = useState(false);

  // Check if user is admin
  const isAdmin = () => {
    const chatbotUser = localStorage.getItem('chatbotUser');
    let userEmail = '';
    
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        userEmail = user.email || '';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    
    if (!userEmail) {
      userEmail = localStorage.getItem('userEmail') || '';
    }
    
    return userEmail.toLowerCase() === 'info@urcommunity.com.au';
  };

  useEffect(() => {
    console.log('🔄 [USE EFFECT] Component mounted, checking admin status...');
    const adminStatus = isAdmin();
    console.log('🔍 [USE EFFECT] Admin status:', adminStatus);
    
    if (!adminStatus) {
      console.log('❌ [USE EFFECT] Not admin user');
      setError('Access denied. Admin privileges required.');
      setLoading(false); // Make sure loading is false
      return;
    }
    console.log('✅ [USE EFFECT] Admin user confirmed, fetching agents...');
    fetchAgents();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const container = document.querySelector('[data-dropdown-container]');
      if (container && !container.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const fetchAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 [FETCH AGENTS] Starting to fetch agents...');
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
          console.log('📧 [FETCH AGENTS] Got email from chatbotUser:', userEmail);
        } catch (e) {
          console.error('❌ [FETCH AGENTS] Error parsing chatbotUser:', e);
        }
      }
      
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
        console.log('📧 [FETCH AGENTS] Got email from userEmail:', userEmail);
      }

      if (!userEmail) {
        throw new Error('User email not found. Please log in again.');
      }

      console.log('🌐 [FETCH AGENTS] Making request to:', `${API_BASE_URL}/api/admin/retell-agents?email=${encodeURIComponent(userEmail)}`);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${API_BASE_URL}/api/admin/retell-agents?email=${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      console.log('📡 [FETCH AGENTS] Response status:', response.status, response.statusText);
      console.log('📡 [FETCH AGENTS] Response ok:', response.ok);

      if (!response.ok) {
        // Try to get error details
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          console.error('❌ [FETCH AGENTS] Error response:', errorData);
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseErr) {
          console.error('❌ [FETCH AGENTS] Could not parse error response');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('📊 [FETCH AGENTS] Response data:', data);
      
      if (data.success) {
        const agentsList = data.agents || [];
        console.log(`✅ [FETCH AGENTS] Successfully fetched ${agentsList.length} agents`);
        setAgents(agentsList);
        
        // If no agents found, log it but don't show as error - this is normal
        if (agentsList.length === 0) {
          console.log('ℹ️ [FETCH AGENTS] No agents found in database for admin email. This is normal if no agents have been assigned yet.');
        }
      } else {
        throw new Error(data.error || 'Failed to fetch agents');
      }
    } catch (err) {
      console.error('❌ [FETCH AGENTS] Error:', err);
      console.error('❌ [FETCH AGENTS] Error message:', err.message);
      console.error('❌ [FETCH AGENTS] Error stack:', err.stack);
      
      // Always clear loading state
      setLoading(false);
      
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Backend server not running. Please start the server on port 3001.');
      } else if (!err.message.includes('500') && !err.message.includes('status: 500')) {
        // Only show non-500 errors
        setError(err.message);
      } else {
        console.error('❌ [FETCH AGENTS] Server error (500) - not showing to user:', err.message);
        // Don't set error for 500s, but ensure we still show the page
        // Set empty agents array so page can render
        setAgents([]);
      }
    } finally {
      console.log('✅ [FETCH AGENTS] Setting loading to false');
      // Ensure loading is always false
      setLoading(false);
    }
  };

  const fetchAgentDetails = async (agentId) => {
    try {
      setError(null); // Clear any previous errors
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/retell-agents/${agentId}?email=${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        }
      });

      if (!response.ok) {
        // Only set error for actual failures, not for 500s that might be temporary
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 500) {
          console.error('Server error fetching agent details:', errorData);
          // Don't show 500 errors to user, just log them
          return;
        }
        throw new Error(errorData.error || `Failed to fetch agent details: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setAgentDetails(data.agent);
        // Fetch billing details for this agent
        fetchBillingDetails(agentId);
      } else {
        throw new Error(data.error || 'Failed to fetch agent details');
      }
    } catch (err) {
      // Only show non-500 errors
      if (!err.message.includes('500')) {
        setError(err.message);
      } else {
        console.error('Error fetching agent details:', err);
      }
    }
  };

  const fetchBillingDetails = async (agentId) => {
    setLoadingBilling(true);
    try {
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
      }

      // Calculate start and end dates for the selected month/year
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0, 23, 59, 59);

      // Fetch billing from call_logs (voice calls)
      const callsResponse = await fetch(
        `${API_BASE_URL}/api/admin/retell-agents/${agentId}/billing?email=${encodeURIComponent(userEmail)}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-email': userEmail
          }
        }
      );

      let billingData = {
        totalCost: 0,
        callsCount: 0,
        chatsCount: 0,
        callsTotal: 0,
        chatsTotal: 0,
        month: month,
        year: year
      };

      if (callsResponse.ok) {
        const callsData = await callsResponse.json();
        if (callsData.success) {
          billingData = {
            ...billingData,
            ...callsData.billing
          };
        }
      }

      // Also try to fetch from database directly if endpoint doesn't exist
      // We'll calculate it on the frontend as a fallback
      setBillingDetails(billingData);
    } catch (err) {
      console.error('Error fetching billing details:', err);
      // Set default billing data on error
      setBillingDetails({
        totalCost: 0,
        callsCount: 0,
        chatsCount: 0,
        callsTotal: 0,
        chatsTotal: 0,
        month: month,
        year: year
      });
    } finally {
      setLoadingBilling(false);
    }
  };

  // Fetch all-time billing for a specific agent (not filtered by month/year)
  const fetchAgentAllTimeBilling = async (agentId) => {
    try {
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
      }

      // Fetch all-time billing from call_logs (voice calls) - no date filter
      const callsResponse = await fetch(
        `${API_BASE_URL}/api/admin/retell-agents/${agentId}/billing-all-time?email=${encodeURIComponent(userEmail)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-email': userEmail
          }
        }
      );

      let billingData = {
        totalCost: 0,
        callsCount: 0,
        chatsCount: 0,
        callsTotal: 0,
        chatsTotal: 0
      };

      if (callsResponse.ok) {
        const callsData = await callsResponse.json();
        if (callsData.success) {
          billingData = {
            ...billingData,
            ...callsData.billing
          };
        }
      } else {
        // Fallback: Calculate from database directly via API
        // Try to get billing data from call_logs and ai_assistant_call_logs
        try {
          const [callsData, chatsData] = await Promise.all([
            fetch(`${API_BASE_URL}/api/admin/billing/calls?agent_id=${agentId}&email=${encodeURIComponent(userEmail)}`, {
              headers: { 'x-admin-email': userEmail }
            }).then(r => r.ok ? r.json() : { total: 0, count: 0 }),
            fetch(`${API_BASE_URL}/api/admin/billing/chats?agent_id=${agentId}&email=${encodeURIComponent(userEmail)}`, {
              headers: { 'x-admin-email': userEmail }
            }).then(r => r.ok ? r.json() : { total: 0, count: 0 })
          ]);

          billingData = {
            callsCount: callsData.count || 0,
            callsTotal: callsData.total || 0,
            chatsCount: chatsData.count || 0,
            chatsTotal: chatsData.total || 0,
            totalCost: (callsData.total || 0) + (chatsData.total || 0)
          };
        } catch (fallbackError) {
          console.error('Fallback billing calculation failed:', fallbackError);
        }
      }

      return billingData;
    } catch (err) {
      console.error('Error fetching all-time billing for agent:', err);
      return {
        totalCost: 0,
        callsCount: 0,
        chatsCount: 0,
        callsTotal: 0,
        chatsTotal: 0
      };
    }
  };

  // Fetch all agents for admin and their billing
  const fetchAdminAgentsWithBilling = async () => {
    setLoadingAdminAgents(true);
    setError(null);
    try {
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
      }

      // Fetch agents for admin
      const response = await fetch(`${API_BASE_URL}/api/admin/retell-agents?email=${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch admin agents: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.agents) {
        const agentsList = data.agents || [];
        setAdminAgents(agentsList);

        // Fetch billing for each agent
        const billingPromises = agentsList.map(async (agent) => {
          const agentId = agent.agent_id || agent.chatbot_id || agent.id;
          if (agentId) {
            const billing = await fetchAgentAllTimeBilling(agentId);
            return { agentId, billing };
          }
          return null;
        });

        const billingResults = await Promise.all(billingPromises);
        const billingMap = {};
        billingResults.forEach(result => {
          if (result && result.agentId) {
            billingMap[result.agentId] = result.billing;
          }
        });

        setAdminAgentsBilling(billingMap);
      } else {
        throw new Error(data.error || 'Failed to fetch admin agents');
      }
    } catch (err) {
      console.error('Error fetching admin agents with billing:', err);
      if (!err.message.includes('500')) {
        setError(err.message);
      }
    } finally {
      setLoadingAdminAgents(false);
    }
  };

  const handleAgentSelect = async (agent) => {
    setSelectedAgent(agent);
    setSearchTerm(agent.agent_name || agent.business_name || agent.name || agent.agent_id || agent.chatbot_id || agent.id || '');
    setShowDropdown(false);
    const agentId = agent.agent_id || agent.chatbot_id || agent.id;
    
    if (agentId) {
      // If agent is from Retell API, assign it to admin and sync calls
      if (showRetellAgents) {
        try {
          setLoading(true);
          const chatbotUser = localStorage.getItem('chatbotUser');
          let userEmail = '';
          
          if (chatbotUser) {
            try {
              const user = JSON.parse(chatbotUser);
              userEmail = user.email || '';
            } catch (e) {
              console.error('Error parsing chatbotUser:', e);
            }
          }
          
          if (!userEmail) {
            userEmail = localStorage.getItem('userEmail') || '';
          }

          console.log('🔄 Assigning agent to admin and syncing calls...');
          const assignResponse = await fetch(`${API_BASE_URL}/api/admin/retell-agents/assign-and-sync?email=${encodeURIComponent(userEmail)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-email': userEmail
            },
            body: JSON.stringify({
              agent_id: agentId,
              agent_name: agent.agent_name || agent.name || agent.business_name,
              agent_data: agent
            })
          });

          if (!assignResponse.ok) {
            throw new Error(`Failed to assign agent: ${assignResponse.status}`);
          }

          const assignData = await assignResponse.json();
          if (assignData.success) {
            console.log('✅ Agent assigned and calls synced:', assignData);
            // Clear any errors on success
            setError(null);
            // Show success message (we'll use a temporary success state)
            let successMsg = `Agent assigned successfully! Synced ${assignData.calls?.synced || 0} new calls, skipped ${assignData.calls?.skipped || 0} existing calls.`;
            if (assignData.chats) {
              successMsg += ` Synced ${assignData.chats?.synced || 0} new chats, skipped ${assignData.chats?.skipped || 0} existing chats.`;
            }
            alert(successMsg); // You can replace this with a better notification component
            // Refresh agents list to show the newly assigned agent
            fetchAgents();
          } else {
            throw new Error(assignData.error || 'Failed to assign agent');
          }
        } catch (err) {
          console.error('Error assigning agent:', err);
          // Only show non-500 errors
          if (!err.message.includes('500') && !err.message.includes('status: 500')) {
            setError(err.message || 'Failed to assign agent and sync calls');
          } else {
            console.error('Server error during agent assignment:', err);
          }
        } finally {
          setLoading(false);
        }
      }
      
      // Fetch agent details
      fetchAgentDetails(agentId);
    } else {
      // If it's a Retell API agent, set the details directly
      if (showRetellAgents) {
        setAgentDetails(agent);
        // Still try to fetch billing details if we have an agent_id
        const agentId = agent.agent_id || agent.chatbot_id || agent.id;
        if (agentId) {
          fetchBillingDetails(agentId);
        }
      }
    }
  };

  const fetchAgentsFromRetell = async () => {
    setLoadingRetellAgents(true);
    setError(null);
    try {
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
      }

      console.log('🔍 [FRONTEND] Fetching agents from Retell API...');
      console.log('🔍 [FRONTEND] API_BASE_URL:', API_BASE_URL);
      console.log('🔍 [FRONTEND] User Email:', userEmail);
      
      const url = `${API_BASE_URL}/api/admin/retell-agents/list-from-retell?email=${encodeURIComponent(userEmail)}`;
      console.log('🔍 [FRONTEND] Request URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        }
      });

      console.log('🔍 [FRONTEND] Response status:', response.status);
      console.log('🔍 [FRONTEND] Response ok:', response.ok);

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          console.error('🔍 [FRONTEND] Error response data:', errorData);
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If response is not JSON, try to get text
          try {
            const errorText = await response.text();
            console.error('🔍 [FRONTEND] Error response text:', errorText);
            if (errorText) {
              errorMessage = `${errorMessage} - ${errorText}`;
            }
          } catch (textError) {
            console.error('🔍 [FRONTEND] Could not parse error response:', textError);
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('🔍 [FRONTEND] Response data:', data);
      
      if (data.success) {
        console.log('✅ [FRONTEND] Success! Agents count:', data.agents?.length || 0);
        setRetellAgents(data.agents || []);
        setShowRetellAgents(true);
      } else {
        throw new Error(data.error || 'Failed to fetch agents from Retell API');
      }
    } catch (err) {
      console.error('❌ [FRONTEND] Error in fetchAgentsFromRetell:', err);
      console.error('❌ [FRONTEND] Error message:', err.message);
      console.error('❌ [FRONTEND] Error stack:', err.stack);
      
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Backend server not running or connection failed. Please check if the server is running on port 3001.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoadingRetellAgents(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const agentsToDisplay = showRetellAgents ? retellAgents : agents;
  
  const filteredAgents = agentsToDisplay.filter(agent => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (agent.agent_id || '').toLowerCase().includes(searchLower) ||
      (agent.chatbot_id || '').toLowerCase().includes(searchLower) ||
      (agent.user_email || '').toLowerCase().includes(searchLower) ||
      (agent.agent_name || '').toLowerCase().includes(searchLower) ||
      (agent.business_name || '').toLowerCase().includes(searchLower) ||
      (agent.name || '').toLowerCase().includes(searchLower)
    );
  });

  if (!isAdmin()) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2 style={{ color: '#dc2626' }}>Access Denied</h2>
        <p>Admin privileges required to access this page.</p>
      </div>
    );
  }

  return (
    <div style={{
      background: '#f9fafb',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px'
          }}>
            <div>
              <h1 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>
                Retell Agents Details
              </h1>
              <p style={{
                fontSize: '0.875rem',
                color: '#6b7280',
                margin: 0
              }}>
                View and manage all Retell agents in the system
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', position: 'relative' }}>
              {/* Search Bar with Dropdown */}
              <div style={{ position: 'relative', minWidth: '300px' }} data-dropdown-container>
                <div style={{ display: 'flex' }}>
                  <input
                    type="text"
                    placeholder="Search and select agent..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                      if (e.target.value === '') {
                        setSelectedAgent(null);
                        setAgentDetails(null);
                      }
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && filteredAgents.length > 0) {
                        handleAgentSelect(filteredAgents[0]);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px 0 0 8px',
                      fontSize: '14px',
                      borderRight: 'none',
                      outline: 'none'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0 8px 8px 0',
                      background: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      borderLeft: 'none'
                    }}
                  >
                    <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`} style={{ fontSize: '12px', color: '#6b7280' }}></i>
                  </button>
                </div>
                {showDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginTop: '4px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {searchTerm ? (
                      filteredAgents.length > 0 ? (
                        filteredAgents.map((agent) => {
                          const agentId = agent.agent_id || agent.chatbot_id || agent.id;
                          return (
                            <div
                              key={agentId}
                              onClick={() => handleAgentSelect(agent)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f3f4f6',
                                fontSize: '13px'
                              }}
                              onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.background = 'white'}
                            >
                              <div style={{ fontWeight: '500', color: '#1f2937' }}>
                                {agent.agent_name || agent.business_name || agent.name || 'Unnamed Agent'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                {agent.agent_id || agent.chatbot_id || agent.id || 'N/A'}
                                {agent.user_email && ` • ${agent.user_email}`}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                          No agents found
                        </div>
                      )
                    ) : (
                      <>
                        {agentsToDisplay.map((agent) => {
                          const agentId = agent.agent_id || agent.chatbot_id || agent.id;
                          return (
                            <div
                              key={agentId}
                              onClick={() => handleAgentSelect(agent)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f3f4f6',
                                fontSize: '13px'
                              }}
                              onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.background = 'white'}
                            >
                              <div style={{ fontWeight: '500', color: '#1f2937' }}>
                                {agent.agent_name || agent.business_name || agent.name || 'Unnamed Agent'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                {agent.agent_id || agent.chatbot_id || agent.id || 'N/A'}
                                {agent.user_email && ` • ${agent.user_email}`}
                              </div>
                            </div>
                          );
                        })}
                        {agentsToDisplay.length === 0 && (
                          <div style={{ padding: '20px 12px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                            {showRetellAgents ? (
                              <>
                                <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                                No agents found from Retell API. Try clicking "List from Retell API" to refresh.
                              </>
                            ) : (
                              <>
                                <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                                No agents found in database. Use "List from Retell API" to fetch agents, or assign an agent to see it here.
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={fetchAgentsFromRetell}
                disabled={loadingRetellAgents}
                style={{
                  padding: '10px 20px',
                  background: loadingRetellAgents ? '#9ca3af' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loadingRetellAgents ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (!loadingRetellAgents) {
                    e.target.style.background = '#6d28d9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loadingRetellAgents) {
                    e.target.style.background = '#7c3aed';
                  }
                }}
              >
                {loadingRetellAgents ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt"></i>
                    <span>List from Retell API</span>
                  </>
                )}
              </button>
            </div>
          </div>
          {showRetellAgents && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: '#ede9fe',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: '#6b21a8'
            }}>
              <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
              Showing {retellAgents.length} agents from Retell API. 
              <button
                onClick={() => {
                  setShowRetellAgents(false);
                  setRetellAgents([]);
                }}
                style={{
                  marginLeft: '8px',
                  background: 'none',
                  border: 'none',
                  color: '#6b21a8',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Switch back to database agents
              </button>
            </div>
          )}
        </div>

        {/* Error Message - Only show non-500 errors */}
        {error && !error.includes('500') && !error.includes('status: 500') && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            color: '#dc2626',
            marginBottom: '16px'
          }}>
            {error}
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                float: 'right',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#7c3aed' }}></i>
          </div>
        )}

        {/* Agent Selection Section - Month/Year Selectors */}
        {!loading && (
          <div style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            flexShrink: 0
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
              Billing Period Selection
            </h4>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={month}
                  onChange={(e) => {
                    const selectedMonth = parseInt(e.target.value);
                    setMonth(selectedMonth);
                    // Refresh billing if agent is selected
                    if (selectedAgent) {
                      const agentId = selectedAgent.agent_id || selectedAgent.chatbot_id || selectedAgent.id;
                      if (agentId) {
                        fetchBillingDetails(agentId);
                      }
                    }
                  }}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('en-AU', { month: 'long' })}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => {
                    setYear(parseInt(e.target.value));
                    // Refresh billing if agent is selected
                    if (selectedAgent) {
                      const agentId = selectedAgent.agent_id || selectedAgent.chatbot_id || selectedAgent.id;
                      if (agentId) {
                        fetchBillingDetails(agentId);
                      }
                    }
                  }}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    width: '100px'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Admin Agents Billing Section */}
        {!loading && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <div>
                <h2 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 8px 0'
                }}>
                  Admin Agents - All-Time Billing
                </h2>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  margin: 0
                }}>
                  View billing summary for all agents under admin account (not filtered by month/year)
                </p>
              </div>
              <button
                onClick={fetchAdminAgentsWithBilling}
                disabled={loadingAdminAgents}
                style={{
                  padding: '10px 20px',
                  background: loadingAdminAgents ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loadingAdminAgents ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!loadingAdminAgents) {
                    e.target.style.background = '#059669';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loadingAdminAgents) {
                    e.target.style.background = '#10b981';
                  }
                }}
              >
                {loadingAdminAgents ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt"></i>
                    <span>Load Admin Agents Billing</span>
                  </>
                )}
              </button>
            </div>

            {loadingAdminAgents ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#7c3aed' }}></i>
                <p style={{ marginTop: '12px', color: '#6b7280' }}>Loading agents and billing data...</p>
              </div>
            ) : adminAgents.length > 0 ? (
              <div style={{
                overflowX: 'auto'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Agent Name</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Agent ID</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Voice Calls</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Calls Cost</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Chat Sessions</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Chats Cost</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151'
                      }}>Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminAgents.map((agent) => {
                      const agentId = agent.agent_id || agent.chatbot_id || agent.id;
                      const billing = adminAgentsBilling[agentId] || {
                        totalCost: 0,
                        callsCount: 0,
                        chatsCount: 0,
                        callsTotal: 0,
                        chatsTotal: 0
                      };
                      return (
                        <tr
                          key={agentId}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.target.closest('tr').style.background = '#f9fafb'}
                          onMouseLeave={(e) => e.target.closest('tr').style.background = 'white'}
                          onClick={() => handleAgentSelect(agent)}
                        >
                          <td style={{
                            padding: '12px',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {agent.agent_name || agent.business_name || agent.name || 'Unnamed Agent'}
                          </td>
                          <td style={{
                            padding: '12px',
                            fontSize: '12px',
                            color: '#6b7280',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all'
                          }}>
                            {agentId || 'N/A'}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'right',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {billing.callsCount || 0}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'right',
                            fontSize: '14px',
                            color: '#111827',
                            fontWeight: '500'
                          }}>
                            ${(billing.callsTotal || 0).toFixed(2)}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'right',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {billing.chatsCount || 0}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'right',
                            fontSize: '14px',
                            color: '#111827',
                            fontWeight: '500'
                          }}>
                            ${(billing.chatsTotal || 0).toFixed(2)}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'right',
                            fontSize: '14px',
                            color: '#111827',
                            fontWeight: '600'
                          }}>
                            ${(billing.totalCost || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{
                      background: '#f0f9ff',
                      borderTop: '2px solid #bae6fd'
                    }}>
                      <td colSpan="2" style={{
                        padding: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0c4a6e'
                      }}>
                        Total
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0c4a6e'
                      }}>
                        {Object.values(adminAgentsBilling).reduce((sum, b) => sum + (b.callsCount || 0), 0)}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0c4a6e'
                      }}>
                        ${Object.values(adminAgentsBilling).reduce((sum, b) => sum + (b.callsTotal || 0), 0).toFixed(2)}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0c4a6e'
                      }}>
                        {Object.values(adminAgentsBilling).reduce((sum, b) => sum + (b.chatsCount || 0), 0)}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0c4a6e'
                      }}>
                        ${Object.values(adminAgentsBilling).reduce((sum, b) => sum + (b.chatsTotal || 0), 0).toFixed(2)}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#0c4a6e'
                      }}>
                        ${Object.values(adminAgentsBilling).reduce((sum, b) => sum + (b.totalCost || 0), 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <i className="fas fa-info-circle" style={{ fontSize: '32px', marginBottom: '12px', color: '#d1d5db' }}></i>
                <p style={{ margin: 0 }}>Click "Load Admin Agents Billing" to view all agents and their billing summary</p>
              </div>
            )}
          </div>
        )}

        {/* Main Content - Agent Details */}
        {!loading && (
          <div>
            {selectedAgent ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                  <div style={{
                    padding: '24px',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#f9fafb'
                  }}>
                    <h2 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#111827',
                      margin: '0 0 8px 0'
                    }}>
                      Agent Details
                    </h2>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      margin: 0
                    }}>
                      {selectedAgent.agent_id || selectedAgent.chatbot_id || selectedAgent.id || 'N/A'}
                    </p>
                  </div>
                  <div style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Agent Name */}
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                          Agent Name
                        </label>
                        <div style={{
                          padding: '10px 12px',
                          background: '#f9fafb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          color: '#111827'
                        }}>
                          {selectedAgent.agent_name || selectedAgent.business_name || selectedAgent.name || 'N/A'}
                        </div>
                      </div>

                      {/* Agent ID */}
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                          Agent ID
                        </label>
                        <div style={{
                          padding: '10px 12px',
                          background: '#f9fafb',
                          borderRadius: '6px',
                          fontSize: '14px',
                          color: '#111827',
                          wordBreak: 'break-all'
                        }}>
                          {selectedAgent.agent_id || selectedAgent.chatbot_id || selectedAgent.id || 'N/A'}
                        </div>
                      </div>

                      {/* Billing Details Section */}
                      <div style={{
                        marginTop: '20px',
                        padding: '20px',
                        background: '#f0f9ff',
                        borderRadius: '8px',
                        border: '1px solid #bae6fd'
                      }}>
                        <h3 style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#0c4a6e',
                          margin: '0 0 16px 0'
                        }}>
                          Billing Details - {new Date(year, month - 1).toLocaleString('en-AU', { month: 'long', year: 'numeric' })}
                        </h3>
                        {loadingBilling ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <i className="fas fa-spinner fa-spin" style={{ fontSize: '20px', color: '#7c3aed' }}></i>
                            <p style={{ marginTop: '8px', color: '#6b7280' }}>Loading billing details...</p>
                          </div>
                        ) : billingDetails ? (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                            <div style={{
                              padding: '12px',
                              background: 'white',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Cost</div>
                              <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                                ${(billingDetails.totalCost || 0).toFixed(2)}
                              </div>
                            </div>
                            <div style={{
                              padding: '12px',
                              background: 'white',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Voice Calls</div>
                              <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                                {billingDetails.callsCount || 0}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                ${(billingDetails.callsTotal || 0).toFixed(2)}
                              </div>
                            </div>
                            <div style={{
                              padding: '12px',
                              background: 'white',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Chat Sessions</div>
                              <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                                {billingDetails.chatsCount || 0}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                ${(billingDetails.chatsTotal || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                            No billing data available for this period
                          </div>
                        )}
                      </div>

                      {/* User Email */}
                      {(agentDetails?.user_email || selectedAgent.user_email) && (
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                            User Email
                          </label>
                          <div style={{
                            padding: '10px 12px',
                            background: '#f9fafb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {agentDetails?.user_email || selectedAgent.user_email}
                          </div>
                        </div>
                      )}

                      {/* Business Name */}
                      {(agentDetails?.business_name || selectedAgent.business_name) && (
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                            Business Name
                          </label>
                          <div style={{
                            padding: '10px 12px',
                            background: '#f9fafb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {agentDetails?.business_name || selectedAgent.business_name || 'N/A'}
                          </div>
                        </div>
                      )}

                      {/* Business Type */}
                      {(agentDetails?.business_type || selectedAgent.business_type) && (
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                            Business Type
                          </label>
                          <div style={{
                            padding: '10px 12px',
                            background: '#f9fafb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {agentDetails?.business_type || selectedAgent.business_type || 'N/A'}
                          </div>
                        </div>
                      )}

                      {/* Phone Number */}
                      {(agentDetails?.phone_number || selectedAgent.phone_number) && (
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                            Phone Number
                          </label>
                          <div style={{
                            padding: '10px 12px',
                            background: '#f9fafb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {agentDetails?.phone_number || selectedAgent.phone_number}
                          </div>
                        </div>
                      )}

                      {/* Created At */}
                      {(agentDetails?.created_at || agentDetails?.timestamp || selectedAgent.created_at) && (
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                            Created At
                          </label>
                          <div style={{
                            padding: '10px 12px',
                            background: '#f9fafb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#111827'
                          }}>
                            {formatDate(agentDetails?.created_at || agentDetails?.timestamp || selectedAgent.created_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            ) : (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '80px 40px',
                textAlign: 'center'
              }}>
                <i className="fas fa-robot" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}></i>
                <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
                  Select an agent from the dropdown to view details
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RetellAgentsDetails;
